import React, { useState, useEffect } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const RetirementCalculator = ({ 
  currentAge, 
  retirementAge, 
  monthlyIncome,
  preRetirementInflation,
  postRetirementInflation,
  onCalculate 
}) => {
  const [desiredMonthlyIncome, setDesiredMonthlyIncome] = useState('50000');
  const [lifeExpectancy, setLifeExpectancy] = useState('80');
  const [expectedReturns, setExpectedReturns] = useState('10');
  const [errors, setErrors] = useState({});

  const formatIndianCurrency = (amount) => {
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) return '₹0';
    return '₹' + numAmount?.toLocaleString('en-IN', { maximumFractionDigits: 0 });
  };

  const validateInputs = () => {
    const newErrors = {};
    
    if (!desiredMonthlyIncome || parseFloat(desiredMonthlyIncome) <= 0) {
      newErrors.desiredMonthlyIncome = 'Please enter a valid monthly income requirement';
    }
    
    if (!lifeExpectancy || parseFloat(lifeExpectancy) <= retirementAge) {
      newErrors.lifeExpectancy = 'Life expectancy must be greater than retirement age';
    }
    
    if (!expectedReturns || parseFloat(expectedReturns) <= 0 || parseFloat(expectedReturns) > 20) {
      newErrors.expectedReturns = 'Expected returns should be between 0% and 20%';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const calculateRetirementCorpus = () => {
    if (!validateInputs()) return;

    const yearsToRetirement = retirementAge - currentAge;
    const yearsInRetirement = parseFloat(lifeExpectancy) - retirementAge;
    const monthlyIncomeNeeded = parseFloat(desiredMonthlyIncome);
    const annualReturns = parseFloat(expectedReturns) / 100;
    const preInflation = preRetirementInflation / 100;
    const postInflation = postRetirementInflation / 100;

    // Calculate future value of monthly income at retirement
    const futureMonthlyIncome = monthlyIncomeNeeded * Math.pow(1 + preInflation, yearsToRetirement);
    const annualIncomeAtRetirement = futureMonthlyIncome * 12;

    // Calculate corpus needed using present value of annuity formula
    const realReturnRate = (annualReturns - postInflation) / (1 + postInflation);
    const corpusNeeded = annualIncomeAtRetirement * ((1 - Math.pow(1 + realReturnRate, -yearsInRetirement)) / realReturnRate);

    // Calculate monthly savings required
    const monthlyRate = annualReturns / 12;
    const totalMonths = yearsToRetirement * 12;
    const monthlySavingsRequired = (corpusNeeded * monthlyRate) / (Math.pow(1 + monthlyRate, totalMonths) - 1);

    onCalculate({
      corpusNeeded: Math.round(corpusNeeded),
      // Provide totalCorpusRequired for downstream components (life goals, dashboard, PDF)
      totalCorpusRequired: Math.round(corpusNeeded),
      monthlySavingsRequired: Math.round(monthlySavingsRequired),
      futureMonthlyIncome: Math.round(futureMonthlyIncome),
      // Also provide a requiredMonthlyIncome alias for PDF exports
      requiredMonthlyIncome: Math.round(futureMonthlyIncome),
      yearsToRetirement,
      yearsInRetirement
    });
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-accent/10">
          <Icon name="Calculator" size={20} className="text-accent" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground">Retirement Calculator</h3>
          <p className="text-sm text-muted-foreground">Calculate your retirement corpus requirement</p>
        </div>
      </div>
      <div className="space-y-4">
        <Input
          label="Desired Monthly Income (Today's Value)"
          type="number"
          placeholder="50000"
          value={desiredMonthlyIncome}
          onChange={(e) => setDesiredMonthlyIncome(e?.target?.value)}
          error={errors?.desiredMonthlyIncome}
          description={`Current value: ${formatIndianCurrency(desiredMonthlyIncome)}`}
          required
        />

        <Input
          label="Life Expectancy (Years)"
          type="number"
          placeholder="80"
          value={lifeExpectancy}
          onChange={(e) => setLifeExpectancy(e?.target?.value)}
          error={errors?.lifeExpectancy}
          description="Expected age until which you'll need retirement income"
          required
        />

        <Input
          label="Expected Annual Returns (%)"
          type="number"
          placeholder="10"
          value={expectedReturns}
          onChange={(e) => setExpectedReturns(e?.target?.value)}
          error={errors?.expectedReturns}
          description="Expected returns from your retirement investments"
          required
        />

        <div className="pt-4">
          <Button 
            variant="default" 
            fullWidth 
            iconName="TrendingUp" 
            iconPosition="left"
            onClick={calculateRetirementCorpus}
          >
            Calculate Retirement Plan
          </Button>
        </div>
      </div>
      <div className="mt-6 p-4 bg-muted/50 rounded-lg">
        <div className="flex items-start gap-2">
          <Icon name="Info" size={16} className="text-primary mt-0.5 flex-shrink-0" />
          <p className="text-xs text-muted-foreground">
            Calculations use compound interest with inflation adjustments. The corpus is calculated to provide inflation-adjusted income throughout your retirement years.
          </p>
        </div>
      </div>
    </div>
  );
};

export default RetirementCalculator;